import { type KeyboardEvent, useEffect, useRef } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardProviderOption } from '@webapp/platform/banking'
import messages from './messages'
import { useMutateCreateCreditCardProvider } from '../../models'

interface UseCreditCardProviderInlineCreatePresenterParams {
  name: string
  onCreated: (provider: CreditCardProviderOption) => void
}

export const useCreditCardProviderInlineCreatePresenter = ({
  name,
  onCreated,
}: UseCreditCardProviderInlineCreatePresenterParams) => {
  const intl = useProcurifyIntl()

  const nameInputRef = useRef<HTMLInputElement>(null)

  const { createProvider, isSaving, hasCreateFailed } =
    useMutateCreateCreditCardProvider()

  // The name field is disabled while saving, which drops its focus. Once a
  // failed create re-enables it, put the user back in it to retry.
  useEffect(() => {
    if (hasCreateFailed) nameInputRef.current?.focus()
  }, [hasCreateFailed])

  const handleCreate = async () => {
    const providerName = name.trim()
    if (!providerName) return

    try {
      const provider = await createProvider({ name: providerName })

      onCreated(provider)

      toast.success({
        title: intl.formatMessage(messages.createSuccessToastTitle),
      })
    } catch (err) {
      toast.error({
        title: intl.formatMessage(messages.createErrorToastTitle),
        children:
          getMessageFromError(err) ??
          intl.formatMessage(messages.createErrorToastMessage),
      })
    }
  }

  return {
    refs: {
      nameInputRef,
    },
    states: {
      isSaving,
    },
    handlers: {
      handleCreate,
      // The field renders inside the consumer's <form>; without this, Enter
      // would implicitly submit that outer form instead of creating.
      handleNameKeyDown: (event: KeyboardEvent) => {
        if (event.key !== 'Enter') return

        event.preventDefault()
        handleCreate()
      },
    },
  }
}
